import { localeFor } from "./languages";
import { ui } from "./localization";
import type { Language } from "../i18n";

const isSameCalendarDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

export const formatMessageTimestamp = (
  value: string,
  language: Language,
  current = new Date(),
): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = localeFor(language);
  const time = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
  if (isSameCalendarDay(date, current)) return `${ui(language, "Today")} ${time}`;

  const yesterday = new Date(current);
  yesterday.setDate(current.getDate() - 1);
  if (isSameCalendarDay(date, yesterday)) return `${ui(language, "Yesterday")} ${time}`;

  const datePart = new Intl.DateTimeFormat(locale, {
    ...(date.getFullYear() === current.getFullYear() ? {} : { year: "numeric" as const }),
    month: language === "zh" ? "numeric" : "short",
    day: "numeric",
  }).format(date);
  return `${datePart} ${time}`;
};

export const formatElapsedDuration = (milliseconds: number, language: Language): string => {
  const seconds = Math.max(0, milliseconds) / 1_000;
  if (seconds < 60) {
    const value = new Intl.NumberFormat(localeFor(language), { minimumFractionDigits: seconds < 10 ? 1 : 0, maximumFractionDigits: seconds < 10 ? 1 : 0 }).format(seconds);
    return ui(language, "{0}s", value);
  }

  const roundedSeconds = Math.round(seconds);
  const number = new Intl.NumberFormat(localeFor(language));
  return ui(language, "{0}m {1}s", number.format(Math.floor(roundedSeconds / 60)), number.format(roundedSeconds % 60));
};
